import { Fragment, useEffect, useRef, useState } from 'react'
import { ModalStore } from '../../state/ModalStore'
import ModalContentWrapper from './ModalContentWrapper'
import ModalHeader from './ModalHeader'
import ModalFooter from './ModalFooter'
import {
    classNames,
    formatCurrency,
    isDev,
    isLocal,
    log,
} from '../../helpers/utils'
import { processAddToCart } from '../../helpers/slug'
import { useForm } from 'react-hook-form'
import { ErrorsComponent } from '../ErrorsComponent'
import Loader from '../Loader'
import { useRouter } from 'next/router'
import * as yup from 'yup'

import { Combobox } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import ModalMainWrapper from './ModalMainWrapper'

export default function PurchaseStoreCreditModal({ totalStoreCredits }) {
    const skimModal = ModalStore((state) => state.skim)
    const creditForm = useRef(null)
    const formSubmitButton = useRef(null)
    const [loading, setLoading] = useState(false)

    const router = useRouter()

    const productID = !isDev && !isLocal ? 205861 : 203066

    const multipleQty = 500

    // Create a validation schema using yup
    const schema = yup.object().shape({
        quantity: yup
            .number('Please enter a quantity')
            .required('Please enter a quantity')
            .min(multipleQty, `Quantity must be at least ${multipleQty}.`)
            .test(
                'isMultipleOf500',
                `Quantity must be a multiple of ${multipleQty}.`,
                (value) => value % multipleQty === 0,
            ),
    })

    const {
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            quantity: multipleQty,
        },
    })

    const handleAddToCart = async (data) => {
        try {
            setLoading(true)
            const cartItem = {
                productId: productID,
                variation: {},
                product: {
                    slug: 'store-credit',
                    name: 'Store Credit',
                },
                quantity: 1,
                price: Number(data.quantity),
            }
            await processAddToCart(cartItem) // this add on in add on cart
            skimModal()
            router.push('/cart')
        } catch (e) {
            log(e)
        } finally {
            setLoading(false)
        }
    }

    const onSubmit = async (data) => {
        await handleAddToCart(data)
    }

    const handleFormSubmission = () => {
        formSubmitButton.current.click()
    }

    const onSelect = (price) => {
        setValue('quantity', price)
    }

    return (
        <>
            {loading && <Loader text="Adding to cart..." />}
            <ModalMainWrapper>
                <ModalContentWrapper size="xl">
                    <ModalHeader title="Purchase Store Credit" />
                    <form ref={creditForm} onSubmit={handleSubmit(onSubmit)}>
                        <div className="border-gray-300 px-4 text-center">
                            <div className="py-4 border-y w-full">
                                <p className="text-olm-gray text-sm">
                                    Available Store Credit
                                </p>
                                <p className="text-olm-black text-xl font-semibold">
                                    {formatCurrency(totalStoreCredits, 2)}
                                </p>
                            </div>

                            <div className="text-left justify-between  items-center flex py-2">
                                <div className="w-1/2">
                                    <p className="text-base text-olm">
                                        Add Store Credit
                                    </p>
                                    <p className="text-xs text-olm-gray">
                                        Credits are not refundable
                                    </p>
                                </div>
                                <div className="w-1/2">
                                    <div className="space-x-1 flex items-center">
                                        <div>
                                            <PriceComboBox
                                                onSelect={onSelect}
                                            />
                                            <p className="text-xs py-1 text-olm-gray">
                                                Purchase in $500 increments
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            className="hidden"
                            ref={formSubmitButton}
                            type="submit">
                            {' '}
                            Purchase Store Credit{' '}
                        </button>
                    </form>
                    <ModalFooter
                        isSubmit={true}
                        confirmText="Add to cart"
                        onConfirm={handleFormSubmission}
                    />
                    <div className="text-left">
                        <ErrorsComponent errors={errors} />
                    </div>
                </ModalContentWrapper>
            </ModalMainWrapper>
        </>
    )
}

// Generate numbers from 500 to 50,000 with 500 increments
const numbers = Array.from({ length: 100 }, (_, index) => 500 * (index + 1))

function PriceComboBox({ onSelect }) {
    const [query, setQuery] = useState('')
    const [selectedNumber, setSelectedNumber] = useState(500)

    const filteredNumbers =
        query === ''
            ? numbers
            : numbers.filter((number) => {
                  return number.toString().includes(query)
              })

    useEffect(() => {
        onSelect && onSelect(selectedNumber)
    }, [selectedNumber])

    return (
        <Combobox as="div" value={selectedNumber} onChange={setSelectedNumber}>
            <div className="relative mt-2">
                <Combobox.Input
                    className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-olm sm:text-sm sm:leading-6"
                    onChange={(event) => setQuery(event.target.value)}
                    displayValue={(number) =>
                        number && formatCurrency(number, 0)
                    }
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                    <ChevronDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                    />
                </Combobox.Button>

                {filteredNumbers.length > 0 && (
                    <Combobox.Options className="absolute z-10 mt-1 max-h-28 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredNumbers.map((number) => (
                            <Combobox.Option
                                key={number}
                                value={number}
                                className={({ active }) =>
                                    classNames(
                                        'relative cursor-default select-none py-2 pl-3 pr-9',
                                        active
                                            ? 'bg-olm text-white'
                                            : 'text-gray-900',
                                    )
                                }>
                                {({ active, selected }) => (
                                    <>
                                        <span
                                            className={classNames(
                                                'block truncate',
                                                selected && 'font-semibold',
                                            )}>
                                            {formatCurrency(number, 0)}
                                        </span>

                                        {selected && (
                                            <span
                                                className={classNames(
                                                    'absolute inset-y-0 right-0 flex items-center pr-4',
                                                    active
                                                        ? 'text-white'
                                                        : 'text-olm',
                                                )}>
                                                <CheckIcon
                                                    className="h-5 w-5"
                                                    aria-hidden="true"
                                                />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                )}
            </div>
        </Combobox>
    )
}
